import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@heidi/design-system/accordion';
import { Separator } from '@heidi/design-system/separator';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { HandHelping, LayoutTemplate, Stethoscope, Users } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

const AVAILABLE_ROLES_ITEM = 'available-roles';

const AVAILABLE_ROLES = [
  ['clinician', pricingPlansModalMessages.roleClinician, Stethoscope],
  ['admin', pricingPlansModalMessages.roleAdmin, Users],
  ['assistant', pricingPlansModalMessages.roleAssistant, HandHelping],
  ['template-manager', pricingPlansModalMessages.roleTemplateManager, LayoutTemplate],
] as const;

/** Collapsible "Available roles" footer of the Practice column (both seats). */
export function AvailableRolesSection({ onExpand }: { onExpand: () => void }) {
  return (
    // gap-4 plus the trigger's own `pt-2` is the design's 24px under the rule.
    <div className="flex shrink-0 flex-col gap-4">
      <Separator />
      <Accordion
        type="single"
        collapsible
        onValueChange={(value) => {
          if (value === AVAILABLE_ROLES_ITEM) {
            onExpand();
          }
        }}
      >
        <AccordionItem value={AVAILABLE_ROLES_ITEM} className="border-b-0">
          {/*
            `font-body` is load-bearing: Radix renders the trigger's header as an
            `<h3>`, which newBrand paints in Exposure, and preflight's
            `button { font-family: inherit }` outranks the global `*` body rule.
          */}
          <AccordionTrigger
            className={cn(
              typographyVariant({ variant: 'caption' }),
              'py-2 font-body font-medium text-text-primary hover:no-underline'
            )}
          >
            <FormattedMessage {...pricingPlansModalMessages.availableRoles} />
          </AccordionTrigger>
          <AccordionContent
            className="pb-2"
            onAnimationEnd={(event) => {
              if (event.currentTarget.dataset.state === 'open') {
                event.currentTarget.scrollIntoView({ block: 'end' });
              }
            }}
          >
            <ul className="flex flex-col gap-1.5">
              {AVAILABLE_ROLES.map(([key, role, Icon]) => (
                <li key={key} className="flex items-center gap-2">
                  <Icon
                    size={16}
                    className="size-4 shrink-0 text-text-primary"
                    aria-hidden
                  />
                  <TypographyCaption className="text-text-primary">
                    <FormattedMessage {...role} />
                  </TypographyCaption>
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
